import { AlertTriangle, Check, Eye, Globe, Key, Loader2, Lock, Plus, Sparkles, Trash } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import {
  type KeyState,
  type Provider,
} from "@/entities/keys/api";
import { useDeleteKey, useSetKey, useValidateKeys } from "@/entities/keys/queries";
import { ApiError } from "@/shared/api/http";
import { cn } from "@/shared/lib/cn";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/ui/alert-dialog";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";

interface KeyBlockProps {
  provider: Provider;
  brand: string;
  state: KeyState;
  instructions: React.ReactNode[];
  warning?: React.ReactNode;
}

const PROVIDER_LABELS: Record<Provider, { title: string; color: "purple" | "cyan" }> = {
  openai: { title: "OpenAI", color: "purple" },
  yandex: { title: "Yandex Cloud", color: "cyan" },
};

export function KeyBlock(props: KeyBlockProps): JSX.Element {
  const { provider, brand, state, instructions, warning } = props;
  const meta = PROVIDER_LABELS[provider];
  const setMutation = useSetKey(provider);
  const delMutation = useDeleteKey(provider);
  const validateMutation = useValidateKeys();
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [keyValue, setKeyValue] = React.useState("");

  const status: "ok" | "missing" | "invalid" = !state.set
    ? "missing"
    : state.valid
      ? "ok"
      : "invalid";

  const ProviderIcon = provider === "openai" ? Sparkles : Globe;

  return (
    <div className="glass-card p-6">
      <div className="mb-5 flex items-center gap-4">
        <div
          className={cn(
            "flex h-13 w-13 shrink-0 items-center justify-center rounded-2xl text-white",
            meta.color === "purple"
              ? "bg-gradient-to-br from-purple to-purple-dark shadow-glow-purple"
              : "bg-gradient-to-br from-cyan to-cyan-dark shadow-glow-cyan",
          )}
          style={{ width: 52, height: 52 }}
        >
          <ProviderIcon className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-bold text-white">{meta.title}</h3>
            <KeyStatus status={status} />
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">{brand}</div>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3 rounded-xl border border-white/10 bg-black/30 px-4 py-3">
        <Key className="h-4 w-4 text-muted-foreground" />
        <span
          className={cn(
            "flex-1 truncate font-mono text-sm tracking-wide",
            status === "ok" ? "text-white" : "text-muted-foreground",
          )}
        >
          {state.hint ?? "ключ не установлен"}
        </span>
        <Eye className="h-4 w-4 text-muted-foreground/40" />
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        {state.set ? (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-white/15 bg-white/[0.04]"
              onClick={() => {
                setKeyValue("");
                setEditorOpen(true);
              }}
            >
              Изменить
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-white/15 bg-white/[0.04]"
              disabled={validateMutation.isPending}
              onClick={async () => {
                try {
                  await validateMutation.mutateAsync();
                  toast.success("Ключи проверены");
                } catch (e) {
                  toast.error(
                    e instanceof ApiError ? e.message : "Не удалось проверить",
                  );
                }
              }}
            >
              {validateMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              Проверить
            </Button>
            <div className="flex-1" />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-pink/40 bg-white/[0.04] text-pink hover:text-pink"
                >
                  <Trash className="h-3.5 w-3.5" /> Удалить
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Удалить ключ {meta.title}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Текущие запуски не пострадают, но новые анализы запустить не получится, пока вы не добавите ключ заново.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Отмена</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={async () => {
                      try {
                        await delMutation.mutateAsync();
                        toast.success("Ключ удалён");
                      } catch (e) {
                        toast.error(
                          e instanceof ApiError ? e.message : "Не удалось удалить",
                        );
                      }
                    }}
                  >
                    Удалить
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        ) : (
          <Button
            type="button"
            className="bg-gradient-primary text-white shadow-glow-purple"
            onClick={() => {
              setKeyValue("");
              setEditorOpen(true);
            }}
          >
            <Plus className="h-3.5 w-3.5" /> Добавить ключ
          </Button>
        )}
      </div>

      <details
        open={status === "missing"}
        className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
      >
        <summary className="cursor-pointer text-sm font-semibold text-foreground/90">
          Как получить ключ?
        </summary>
        <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-sm text-foreground/85">
          {instructions.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
        {warning && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber/30 bg-amber/[0.08] p-3 text-xs text-foreground/90">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber" />
            <span>{warning}</span>
          </div>
        )}
      </details>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{state.set ? "Изменить" : "Добавить"} ключ {meta.title}</DialogTitle>
            <DialogDescription>
              Ключ проверится одним пробным вызовом. До успешной проверки ничего не сохранится.
            </DialogDescription>
          </DialogHeader>
          <label className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-foreground/90">Новый ключ</span>
            <div className="flex items-center gap-2 rounded-lg border border-purple bg-black/30 px-3 py-2 shadow-[0_0_0_3px_rgba(139,92,246,0.15)]">
              <Lock className="h-4 w-4 text-purple" />
              <input
                value={keyValue}
                onChange={(e) => setKeyValue(e.target.value)}
                type="password"
                autoFocus
                placeholder={
                  provider === "openai" ? "sk-..." : "AQVN..."
                }
                className="h-9 w-full bg-transparent font-mono text-sm tracking-wider text-white outline-none placeholder:text-muted-foreground"
              />
            </div>
          </label>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="border-white/15 bg-white/[0.04]"
              onClick={() => setEditorOpen(false)}
              disabled={setMutation.isPending}
            >
              Отмена
            </Button>
            <Button
              type="button"
              className="bg-gradient-primary text-white shadow-glow-purple"
              disabled={setMutation.isPending || keyValue.trim().length === 0}
              onClick={async () => {
                try {
                  await setMutation.mutateAsync(keyValue.trim());
                  toast.success(`${meta.title}: ключ принят`);
                  setEditorOpen(false);
                } catch (e) {
                  if (e instanceof ApiError) {
                    if (e.code === "INVALID_API_KEY") {
                      toast.error("Ключ отклонён провайдером");
                    } else if (e.code === "VALIDATION_SERVICE_UNAVAILABLE") {
                      toast.error(
                        "Провайдер сейчас недоступен, попробуйте позже",
                      );
                    } else {
                      toast.error(e.message);
                    }
                  } else {
                    toast.error("Не удалось сохранить");
                  }
                }
              }}
            >
              {setMutation.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Проверяем…
                </>
              ) : (
                "Сохранить"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KeyStatus({ status }: { status: "ok" | "missing" | "invalid" }): JSX.Element {
  const map = {
    ok: { label: "Подключён", color: "text-green", bg: "bg-green/10", border: "border-green/35", Icon: Check },
    missing: { label: "Не настроен", color: "text-muted-foreground", bg: "bg-white/5", border: "border-white/10", Icon: Lock },
    invalid: { label: "Невалиден", color: "text-pink", bg: "bg-pink/10", border: "border-pink/35", Icon: AlertTriangle },
  } as const;
  const s = map[status];
  const Icon = s.Icon;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold", s.bg, s.border, s.color)}>
      <Icon className="h-3 w-3" /> {s.label}
    </span>
  );
}
